/**
 * D9 · Side-by-side comparison of two takes of the same exercise — tangible
 * progress proof. Take A traces blue (#2a78d6), take B orange (#eb6834).
 */

import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { RenderedExercise, TracePoint } from '@solfo/shared';
import { api, fetchFileUrl } from '../../lib/api.ts';
import { Card, ErrorText, Select, Spinner, Tag, fmtDate } from '../../lib/ui.tsx';
import PitchCanvas from '../../components/PitchCanvas.tsx';
import { SERIES_1, SERIES_2 } from '../../components/charts.tsx';

interface TakeLite {
  id: string;
  createdAt: string;
  accuracyScore: number | null;
  status: string;
  fileId: string;
  pitchTraceJson: string | null;
  targetJson: string | null;
}

function TakeAudio({ take, label, color }: { take: TakeLite; label: string; color: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    void fetchFileUrl(take.fileId).then(setUrl).catch(() => {});
  }, [take.fileId]);
  return (
    <div className="flex items-center gap-2">
      <span className="flex items-center gap-1.5 text-xs font-semibold" style={{ color }}>
        <span className="inline-block size-2.5 rounded-full" style={{ background: color }} />
        {label} · {fmtDate(take.createdAt)}
      </span>
      {take.accuracyScore !== null && <Tag>score {take.accuracyScore}</Tag>}
      {url && <audio controls src={url} className="h-8 min-w-0 flex-1" />}
    </div>
  );
}

export default function TakeCompare() {
  const [params, setParams] = useSearchParams();
  const studentId = params.get('student');
  const exerciseId = params.get('exercise');
  const aId = params.get('a');
  const bId = params.get('b');
  const playheadRef = useRef(0);

  const { data: takes } = useQuery({
    queryKey: ['takes-of', studentId, exerciseId],
    enabled: !!studentId && !!exerciseId,
    queryFn: () =>
      api<TakeLite[]>(`/api/takes?studentId=${studentId}&exerciseId=${exerciseId}`),
  });

  const { data: pair, error } = useQuery({
    queryKey: ['compare', aId, bId],
    enabled: !!aId && !!bId,
    queryFn: () => api<{ a: TakeLite; b: TakeLite }>(`/api/takes-compare?a=${aId}&b=${bId}`),
  });

  const candidates = (takes ?? []).filter((t) => t.status !== 'draft');
  const rendered: RenderedExercise | null = pair?.a.targetJson
    ? (JSON.parse(pair.a.targetJson) as RenderedExercise)
    : pair?.b.targetJson
      ? (JSON.parse(pair.b.targetJson) as RenderedExercise)
      : null;
  const traceA: TracePoint[] = pair?.a.pitchTraceJson ? JSON.parse(pair.a.pitchTraceJson) : [];
  const traceB: TracePoint[] = pair?.b.pitchTraceJson ? JSON.parse(pair.b.pitchTraceJson) : [];

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-bold">Compare takes</h1>
      <Card>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs text-slate-500">
            Take A (blue)
            <Select
              value={aId ?? ''}
              onChange={(e) => {
                params.set('a', e.target.value);
                setParams(params);
              }}
            >
              <option value="">choose…</option>
              {candidates.map((t) => (
                <option key={t.id} value={t.id}>
                  {fmtDate(t.createdAt)} {t.accuracyScore !== null ? `· ${t.accuracyScore}` : ''}
                </option>
              ))}
            </Select>
          </label>
          <label className="text-xs text-slate-500">
            Take B (orange)
            <Select
              value={bId ?? ''}
              onChange={(e) => {
                params.set('b', e.target.value);
                setParams(params);
              }}
            >
              <option value="">choose…</option>
              {candidates
                .filter((t) => t.id !== aId)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {fmtDate(t.createdAt)} {t.accuracyScore !== null ? `· ${t.accuracyScore}` : ''}
                  </option>
                ))}
            </Select>
          </label>
        </div>
      </Card>

      {!aId || !bId ? (
        <p className="text-center text-sm text-slate-400">Pick two takes of the same exercise, weeks apart.</p>
      ) : !pair ? (
        error ? <ErrorText error={error} /> : <Spinner />
      ) : (
        <>
          {rendered && (
            <PitchCanvas
              rendered={rendered}
              trace={traceA}
              traceB={traceB}
              currentTime={playheadRef.current}
              mode="full"
              height={260}
            />
          )}
          <Card>
            <div className="space-y-2">
              <TakeAudio take={pair.a} label="A" color={SERIES_1} />
              <TakeAudio take={pair.b} label="B" color={SERIES_2} />
            </div>
            {pair.a.accuracyScore !== null && pair.b.accuracyScore !== null && (
              <p className="mt-3 text-center text-sm font-medium text-slate-600">
                {pair.b.accuracyScore - pair.a.accuracyScore >= 0 ? '📈' : '📉'}{' '}
                {Math.abs(pair.b.accuracyScore - pair.a.accuracyScore)} point
                {Math.abs(pair.b.accuracyScore - pair.a.accuracyScore) === 1 ? '' : 's'}{' '}
                {pair.b.accuracyScore >= pair.a.accuracyScore ? 'improvement' : 'difference'} between takes
              </p>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
